/**
 * 应用注册服务
 * 处理用户在各应用（文件系统、读书系统等）的首次注册逻辑
 */
import { supabase } from '../config/supabase';
import { deriveExternalUserFlag } from '../utils/user-scope';

// 文件系统用户资料类型
export interface FSUserProfile {
  id: string;
  role: 'basic' | 'intermediate' | 'advanced' | 'uploader' | 'admin';
  preferences: {
    sidebar_width?: number;
    sidebar_collapsed?: boolean;
    theme?: 'light' | 'dark' | 'system';
    view_mode?: 'grid' | 'list';
  };
  two_factor_enabled: boolean;
  last_password_change: string | null;
  avatar_storage_path: string | null;
  created_at: string;
  updated_at: string | null;
  is_external: boolean;
}

// 核心用户信息类型
export interface CoreUser {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  phone_number: string | null;
  language_preference: string;
  created_at: string;
  updated_at: string | null;
  last_login_at: string | null;
  is_active: boolean;
  global_preferences: Record<string, unknown>;
}

// 应用类型
export type AppType = 'fs' | 'books' | 'poster' | 'ditu';

/**
 * 确保用户在文件系统已注册
 * 首次访问时自动创建默认资料
 */
export async function ensureFSRegistration(userId: string): Promise<{
  isNewUser: boolean;
  fsProfile: FSUserProfile;
  coreUser: CoreUser | null;
}> {
  try {
    // 1. 尝试获取核心用户信息（可能还不存在）
    let coreUser: CoreUser | null = null;
    const { data: existingCoreUser } = await supabase
      .from('core_users')
      .select('*')
      .eq('id', userId)
      .single();
    
    coreUser = existingCoreUser;

    // 2. 检查文件系统资料是否存在
    const { data: existingProfile } = await supabase
      .from('fs_user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (existingProfile) {
      // 已注册，更新最后活跃时间
      await supabase
        .from('user_app_registrations')
        .update({ last_active_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('app', 'fs');

      return {
        isNewUser: false,
        fsProfile: existingProfile as FSUserProfile,
        coreUser,
      };
    }

    // 3. 首次访问文件系统，创建默认资料
    console.log('🆕 新用户首次访问文件系统，创建默认资料...');
    
    const isExternal = deriveExternalUserFlag(coreUser?.email);

    const defaultProfile = {
      id: userId,
      role: 'basic' as const,
      preferences: {},
      two_factor_enabled: false,
      is_external: isExternal,
    };

    const { data: newProfile, error: insertError } = await supabase
      .from('fs_user_profiles')
      .insert(defaultProfile)
      .select()
      .single();

    if (insertError) {
      console.error('创建文件系统资料失败:', insertError);
      // 如果插入失败，可能是并发创建，尝试再次查询
      const { data: retryProfile } = await supabase
        .from('fs_user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (retryProfile) {
        return {
          isNewUser: false,
          fsProfile: retryProfile as FSUserProfile,
          coreUser,
        };
      }
      throw new Error(`创建文件系统资料失败: ${insertError.message}`);
    }

    // 4. 记录应用注册
    await supabase
      .from('user_app_registrations')
      .upsert({
        user_id: userId,
        app: 'fs',
        registered_at: new Date().toISOString(),
        last_active_at: new Date().toISOString(),
        is_active: true,
      }, {
        onConflict: 'user_id,app',
      });

    console.log('✅ 文件系统用户资料创建成功');

    return {
      isNewUser: true,
      fsProfile: newProfile as FSUserProfile,
      coreUser,
    };
  } catch (error) {
    console.error('确保文件系统注册失败:', error);
    throw error;
  }
}

/**
 * 检查用户是否已在指定应用注册
 */
export async function isUserRegisteredInApp(userId: string, app: AppType): Promise<boolean> {
  const { data } = await supabase
    .from('user_app_registrations')
    .select('user_id')
    .eq('user_id', userId)
    .eq('app', app)
    .single();
  
  return !!data;
}

/**
 * 获取用户注册的所有应用
 */
export async function getUserRegisteredApps(userId: string): Promise<AppType[]> {
  const { data } = await supabase
    .from('user_app_registrations')
    .select('app')
    .eq('user_id', userId)
    .eq('is_active', true);
  
  return (data || []).map(item => item.app as AppType);
}
